import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams, useParams, Link } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  getDoc,
  doc,
  setDoc,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { syncInvoiceToClient } from "../services/invoiceSyncService";
import toast from "react-hot-toast";
import jsPDF from "jspdf";

import SelectMenu from "../components/crm/SelectMenu";
const SEND_INVOICE_URL =
  "https://jurident-feedback-backend-596718606544.us-central1.run.app/send-invoice";
import {
  ChevronRight,
  Plus,
  Trash2,
  Eye,
  Pencil,
  Save,
  Send,
  Download,
  Mail,
  MessageCircle,
  Loader2,
} from "lucide-react";

const TAX_RATE = 18; // GST %

const inr = (n) =>
  "₹" +
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// ---- number → Indian-rupee words ----
const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
const twoDigits = (n) =>
  n < 20 ? ONES[n] : TENS[Math.floor(n / 10)] + (n % 10 ? " " + ONES[n % 10] : "");
const threeDigits = (n) => {
  const h = Math.floor(n / 100);
  const rest = n % 100;
  return (
    (h ? ONES[h] + " Hundred" + (rest ? " " : "") : "") + (rest ? twoDigits(rest) : "")
  );
};
const numberToWords = (num) => {
  const n = Math.floor(Math.abs(num));
  if (n === 0) return "Zero Rupees Only";
  let words = "";
  const crore = Math.floor(n / 10000000);
  const lakh = Math.floor((n % 10000000) / 100000);
  const thousand = Math.floor((n % 100000) / 1000);
  const rest = n % 1000;
  if (crore) words += twoDigits(crore) + " Crore ";
  if (lakh) words += twoDigits(lakh) + " Lakh ";
  if (thousand) words += twoDigits(thousand) + " Thousand ";
  if (rest) words += threeDigits(rest);
  return words.trim() + " Rupees Only";
};

const fmtDate = (s) =>
  s
    ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

const clientAddress = (c) => {
  const a = c?.primaryAddress;
  if (!a) return "";
  return [a.building, a.street, a.city, a.district, a.state, a.pincode, a.country]
    .filter(Boolean)
    .join(", ");
};

// Build a jsPDF invoice matching the on-screen preview. Uses "Rs." for INR
// (the ₹ glyph isn't in jsPDF's default fonts).
const rs = (n) =>
  "Rs. " + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const buildInvoicePdf = (d) => {
  const pdf = new jsPDF();
  const W = pdf.internal.pageSize.getWidth();
  const M = 15;
  let y = 20;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(18);
  pdf.text("JURIDENT", M, y);
  pdf.setFontSize(26);
  pdf.text("INVOICE", W - M, y, { align: "right" });
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(120);
  pdf.text("Legal. Simplified.", M, y + 5);
  pdf.text(d.invoiceNumber || "", W - M, y + 6, { align: "right" });
  pdf.text("Date: " + fmtDate(d.invoiceDate), W - M, y + 11, { align: "right" });
  pdf.text("Due: " + fmtDate(d.dueDate), W - M, y + 16, { align: "right" });
  pdf.setTextColor(0);

  y += 28;
  // Bill To + Case
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.text("BILL TO", M, y);
  if (d.caseTitle) pdf.text("CASE", W / 2, y);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  let ly = y + 6;
  pdf.text(d.billTo?.name || "-", M, ly);
  (pdf.splitTextToSize(d.billTo?.address || "", W / 2 - M - 5) || []).forEach((line) => {
    ly += 5;
    pdf.text(line, M, ly);
  });
  if (d.billTo?.gstin) {
    ly += 5;
    pdf.text("GSTIN: " + d.billTo.gstin, M, ly);
  }
  if (d.caseTitle) {
    let ry = y + 6;
    pdf.splitTextToSize(d.caseTitle, W / 2 - M - 5).forEach((line) => {
      pdf.text(line, W / 2, ry);
      ry += 5;
    });
    if (d.caseData?.caseNo) { pdf.text("Case No: " + d.caseData.caseNo, W / 2, ry); ry += 5; }
    if (d.caseData?.courtName) pdf.text("Court: " + d.caseData.courtName, W / 2, ry);
  }

  // Items table
  y = ly + 14;
  const cols = [M, M + 8, 95, 120, 140, 165, W - M]; // #, desc, relatedTo, qty, amount, tax, total(right)
  pdf.setFillColor(241, 245, 249);
  pdf.rect(M, y - 5, W - 2 * M, 8, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  pdf.text("#", cols[0] + 1, y);
  pdf.text("DESCRIPTION", cols[1], y);
  pdf.text("RELATED TO", cols[2], y);
  pdf.text("QTY", cols[3], y);
  pdf.text("AMOUNT", cols[4], y);
  pdf.text("TAX", cols[5], y);
  pdf.text("TOTAL", cols[6], y, { align: "right" });
  y += 8;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  (d.items || []).forEach((it, i) => {
    pdf.text(String(i + 1), cols[0] + 1, y);
    pdf.text(pdf.splitTextToSize(it.description || "-", 40), cols[1], y);
    pdf.text(pdf.splitTextToSize(it.relatedTo || "-", 22), cols[2], y);
    pdf.text(String(it.qty), cols[3], y);
    pdf.text(it.base.toFixed(2), cols[4], y);
    pdf.text(it.tax.toFixed(2), cols[5], y);
    pdf.text(it.total.toFixed(2), cols[6], y, { align: "right" });
    y += 8;
    pdf.setDrawColor(230);
    pdf.line(M, y - 3, W - M, y - 3);
  });

  // Totals
  y += 4;
  const tx = W - M - 60;
  pdf.setFontSize(9);
  pdf.text("Sub Total", tx, y);
  pdf.text(rs(d.subTotal), W - M, y, { align: "right" });
  if (d.discountAmt > 0) {
    y += 6;
    pdf.text("Discount", tx, y);
    pdf.text("- " + rs(d.discountAmt), W - M, y, { align: "right" });
  }
  y += 6;
  pdf.text("GST", tx, y);
  pdf.text(rs(d.gst), W - M, y, { align: "right" });
  y += 8;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("Total Amount", tx, y);
  pdf.text(rs(d.totalAmount), W - M, y, { align: "right" });
  y += 8;
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(8);
  pdf.setTextColor(120);
  pdf.text(d.amountInWords || "", W - M, y, { align: "right" });

  pdf.setTextColor(150);
  pdf.setFont("helvetica", "normal");
  pdf.text("Thank you for your business!", M, 285);
  pdf.text("Authorized Signatory", W - M, 285, { align: "right" });
  return pdf;
};

function CreateInvoice() {
  const navigate = useNavigate();
  const { globalUser } = useAuth();
  const [searchParams] = useSearchParams();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const presetCaseId = searchParams.get("case");

  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [series, setSeries] = useState("1");
  const [userName, setUserName] = useState("User");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("form"); // form | preview

  // Edit mode: the loaded invoice + its stored status (so "Save Changes" keeps it).
  const [loadedInvoice, setLoadedInvoice] = useState(null);
  const [editStatus, setEditStatus] = useState("sent");

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [invoiceDate, setInvoiceDate] = useState(today);
  const [dueDate, setDueDate] = useState("");
  const [contactId, setContactId] = useState("");
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [discountPct, setDiscountPct] = useState(0);
  const [items, setItems] = useState([
    { id: "1", description: "", relatedTo: "", qty: 1, amount: "" },
  ]);

  useEffect(() => {
    const load = async () => {
      if (!globalUser?.uid) return;
      setLoading(true);
      try {
        // Clients
        const clientSnap = await getDocs(
          query(collection(db, "clients"), where("owner", "==", globalUser.uid))
        );
        setClients(clientSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

        // Cases
        const all = [];
        for (const col of ["pending", "disposed"]) {
          const snap = await getDocs(
            query(collection(db, col), where("owner", "==", globalUser.uid))
          );
          snap.docs.forEach((d) => all.push({ id: d.id, collection: col, ...d.data() }));
        }
        setCases(all);

        // User name
        const uSnap = await getDoc(doc(db, "lawyers", globalUser.uid));
        if (uSnap.exists()) setUserName(uSnap.data().name || "User");

        if (isEdit) {
          // ===== EDIT / VIEW an existing invoice =====
          const invSnap = await getDoc(
            doc(db, "lawyers", globalUser.uid, "invoices", editId)
          );
          if (!invSnap.exists()) {
            toast.error("Invoice not found");
            navigate("/portal/invoices");
            return;
          }
          const inv = { id: invSnap.id, ...invSnap.data() };
          setLoadedInvoice(inv);
          setEditStatus(inv.status || "sent");
          setInvoiceNumber(inv.invoiceNumber || "");
          setInvoiceDate((inv.invoiceDate || inv.raisedOn || today).slice(0, 10));
          setDueDate(inv.dueDate ? inv.dueDate.slice(0, 10) : "");
          setContactId(inv.contactId || "");
          setSelectedCaseId(inv.selectedCase?.id || "");
          setDiscountPct(inv.discountPct || 0);
          const prefItems = (inv.particulars || []).map((p, i) => ({
            id: p.id || String(i + 1),
            description: p.description || "",
            relatedTo: p.relatedTo || "",
            qty: p.qty || 1,
            amount: p.amount != null ? String(p.amount) : "",
          }));
          setItems(
            prefItems.length
              ? prefItems
              : [{ id: "1", description: "", relatedTo: "", qty: 1, amount: "" }]
          );
        } else {
          // ===== CREATE a new invoice =====
          if (presetCaseId && all.some((c) => c.id === presetCaseId))
            setSelectedCaseId(presetCaseId);

          // Series + invoice number
          let cur = "1";
          const sSnap = await getDoc(
            doc(db, "lawyers", globalUser.uid, "settings", "invoiceSeries")
          );
          if (sSnap.exists()) cur = sSnap.data().currentSeries || "1";
          setSeries(cur);
          const yr = new Date(invoiceDate).getFullYear();
          const num = String(cur).replace(/\D/g, "") || "1";
          setInvoiceNumber(`INV-${yr}-${num.padStart(5, "0")}`);
        }
      } catch (e) {
        console.error("CreateInvoice load error:", e);
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [globalUser, editId]); // eslint-disable-line

  // Auto-select a client when creating a new invoice: pick the sole client, or
  // the first one (the "Select a client" placeholder is dropped for 2+ clients,
  // so the state must match the option the browser shows as selected).
  useEffect(() => {
    if (isEdit) return;
    if (!contactId && clients.length > 0) setContactId(clients[0].id);
  }, [clients, isEdit]); // eslint-disable-line

  const selectedClient = clients.find((c) => c.id === contactId) || null;
  const selectedCase = cases.find((c) => c.id === selectedCaseId) || null;

  // ---- line + totals ----
  const withComputed = items.map((it) => {
    const base = (parseFloat(it.amount) || 0) * (parseInt(it.qty, 10) || 0);
    const tax = Math.round(base * TAX_RATE) / 100;
    return { ...it, base, tax, total: base + tax };
  });
  const subTotal = withComputed.reduce((s, it) => s + it.base, 0);
  const discountAmt = Math.round(subTotal * (parseFloat(discountPct) || 0)) / 100;
  const taxable = subTotal - discountAmt;
  const gst = Math.round(taxable * TAX_RATE) / 100;
  const totalAmount = taxable + gst;

  const addItem = () =>
    setItems((prev) => [
      ...prev,
      { id: `${prev.length + 1}-${prev.reduce((s, p) => s + p.id.length, 0)}`, description: "", relatedTo: "", qty: 1, amount: "" },
    ]);
  const updateItem = (id, field, value) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: value } : it)));
  const removeItem = (id) =>
    setItems((prev) => (prev.length > 1 ? prev.filter((it) => it.id !== id) : prev));

  const clientName = selectedClient
    ? `${selectedClient.salutation ? selectedClient.salutation + " " : ""}${selectedClient.name || ""}`.trim()
    : loadedInvoice?.contactName || "";
  const caseTitle = selectedCase
    ? [selectedCase.petitionerName, selectedCase.respondentName].filter(Boolean).join(" vs ") ||
      selectedCase.caseType ||
      ""
    : loadedInvoice?.resourceName || "";

  // Bill-to + case data used by the preview / PDF / save. Falls back to the
  // stored invoice snapshot when the client/case is no longer in the lists
  // (e.g. deleted) so an existing invoice still renders fully.
  const billToObj = selectedClient
    ? {
        name: clientName,
        address: clientAddress(selectedClient),
        gstin: selectedClient?.gstin || "",
        email: selectedClient?.email || "",
      }
    : loadedInvoice?.billTo || { name: clientName, address: "", gstin: "", email: "" };
  const previewCase = selectedCase || loadedInvoice?.selectedCase || null;

  const validate = () => {
    if (!contactId) {
      toast.error("Select a client");
      return false;
    }
    if (!dueDate) {
      toast.error("Select a due date");
      return false;
    }
    if (!withComputed.some((it) => it.description && it.base > 0)) {
      toast.error("Add at least one item with a description and amount");
      return false;
    }
    return true;
  };

  const save = async (status, { email = false } = {}) => {
    if (!validate()) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const particulars = withComputed
        .filter((it) => it.description || it.base > 0)
        .map((it) => ({
          id: it.id,
          description: it.description,
          relatedTo: it.relatedTo || "",
          qty: parseInt(it.qty, 10) || 1,
          amount: parseFloat(it.amount) || 0,
          tax: it.tax,
          taxRate: TAX_RATE,
          lineTotal: it.total,
        }));

      // Fields common to both create + update.
      const core = {
        invoiceNumber,
        invoiceDate,
        dueDate,
        contactId,
        contactName: clientName,
        // Denormalized bill-to so the invoice/preview is self-contained.
        billTo: billToObj,
        resourceType: selectedCase ? "case" : "none",
        resourceName: caseTitle,
        selectedCase: selectedCase
          ? {
              id: selectedCase.id,
              caseNo: selectedCase.caseNo,
              petitionerName: selectedCase.petitionerName,
              respondentName: selectedCase.respondentName,
              courtName: selectedCase.courtName,
            }
          : loadedInvoice?.selectedCase || null,
        currency: "INR",
        particulars,
        subTotal,
        discountPct: parseFloat(discountPct) || 0,
        discountAmount: discountAmt,
        taxRate: TAX_RATE,
        gst,
        totalAmount,
        amountInWords: numberToWords(totalAmount),
        dueAmount: status === "paid" ? 0 : totalAmount,
        status, // draft | sent | paid
        userId: globalUser.uid,
      };

      if (isEdit) {
        // ===== UPDATE existing invoice — keep original raiser/date/timeline =====
        const updatePayload = {
          ...core,
          updatedAt: now,
          timeline: arrayUnion({
            id: Date.now().toString(),
            type: "invoice_updated",
            timestamp: now,
            by: userName,
            changes: {
              "Total Amount": {
                old: inr(loadedInvoice?.totalAmount || 0),
                new: inr(totalAmount),
              },
              "Invoice Status": { old: editStatus, new: status },
            },
          }),
        };
        await updateDoc(
          doc(db, "lawyers", globalUser.uid, "invoices", editId),
          updatePayload
        );
        setEditStatus(status);

        if (contactId && status !== "draft") {
          try {
            await syncInvoiceToClient(
              contactId,
              { ...loadedInvoice, ...core },
              editId,
              globalUser.uid,
              userName
            );
          } catch (e) {
            console.error("Client sync failed:", e);
          }
        }

        if (email) {
          if (!selectedClient?.email) {
            toast.error("Saved, but client has no email — add one in Clients to send.");
          } else {
            const sent = await emailInvoice();
            if (!sent) toast.error("Invoice saved, but the email could not be sent.");
          }
        }

        toast.success(
          status === "paid"
            ? "Invoice marked paid"
            : email
            ? "Invoice sent to client"
            : "Invoice updated"
        );
        navigate("/portal/invoices");
        return;
      }

      // ===== CREATE a new invoice =====
      const payload = {
        ...core,
        raisedBy: userName,
        raisedOn: now,
        createdAt: now,
        timeline: [
          {
            id: Date.now().toString(),
            type: "invoice_created",
            timestamp: now,
            by: userName,
            changes: {
              "Invoice Number": { old: "-", new: invoiceNumber },
              "Total Amount": { old: "-", new: inr(totalAmount) },
              "Invoice Status": { old: "-", new: status },
            },
          },
        ],
      };

      const ref = await addDoc(collection(db, "lawyers", globalUser.uid, "invoices"), payload);

      // Sync to client portal (only for sent/paid — not drafts)
      if (contactId && status !== "draft") {
        try {
          await syncInvoiceToClient(contactId, payload, ref.id, globalUser.uid, userName);
        } catch (e) {
          console.error("Client sync failed:", e);
        }
      }

      // Increment the numeric series
      try {
        const settingsRef = doc(db, "lawyers", globalUser.uid, "settings", "invoiceSeries");
        const m = String(series).match(/^(.*?)(\d+)$/);
        const nextSeries = m ? m[1] + (parseInt(m[2], 10) + 1) : series;
        await setDoc(settingsRef, { currentSeries: nextSeries, updatedAt: now });
      } catch (e) {
        console.error("Series increment failed:", e);
      }

      // Optionally email the PDF to the client (same backend as the old flow).
      if (email) {
        if (!selectedClient?.email) {
          toast.error("Saved, but client has no email — add one in Clients to send.");
        } else {
          const sent = await emailInvoice();
          if (!sent) toast.error("Invoice saved, but the email could not be sent.");
        }
      }

      toast.success(
        status === "draft"
          ? "Draft saved"
          : status === "paid"
          ? "Invoice created & marked paid"
          : email
          ? "Invoice sent to client"
          : "Invoice created"
      );
      navigate("/portal/invoices");
    } catch (e) {
      console.error("Save invoice error:", e);
      toast.error("Failed to save invoice");
    } finally {
      setSaving(false);
    }
  };

  // Snapshot of the current invoice for PDF / email / whatsapp.
  const pdfData = () => ({
    invoiceNumber,
    invoiceDate,
    dueDate,
    billTo: billToObj,
    caseTitle,
    caseData: previewCase,
    items: withComputed.filter((it) => it.description || it.base > 0),
    subTotal,
    discountAmt,
    gst,
    totalAmount,
    amountInWords: numberToWords(totalAmount),
  });

  const downloadPdf = () => {
    if (!withComputed.some((it) => it.description && it.base > 0)) {
      toast.error("Add at least one item first");
      return;
    }
    buildInvoicePdf(pdfData()).save(`${invoiceNumber || "invoice"}.pdf`);
  };

  // Email the invoice PDF to the client via the Cloud Run backend.
  const emailInvoice = async () => {
    try {
      const pdf = buildInvoicePdf(pdfData());
      const pdfBase64 = pdf.output("datauristring").split(",")[1];
      const res = await fetch(SEND_INVOICE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientEmail: selectedClient?.email || billToObj.email,
          clientName,
          lawyerName: userName,
          lawyerEmail: globalUser?.email,
          invoiceNumber,
          invoiceAmount: totalAmount,
          invoiceCurrency: "₹",
          caseNumber: selectedCase?.caseNo || null,
          caseName: caseTitle || null,
          pdfBase64,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "send failed");
      return true;
    } catch (e) {
      console.error("Email invoice error:", e);
      return false;
    }
  };

  // Standalone "Share via Email" (from preview): save as sent + email.
  const shareEmail = () => {
    if (!selectedClient?.email && !billToObj.email) {
      toast.error("This client has no email. Add one in Clients first.");
      return;
    }
    save("sent", { email: true });
  };

  // Share a text summary + phone via WhatsApp (no PDF attach on wa.me).
  const shareWhatsApp = () => {
    if (!validate()) return;
    const raw = (selectedClient?.contact || "").replace(/\D/g, "");
    const phone = raw.length === 10 ? "91" + raw : raw;
    const msg =
      `*Invoice ${invoiceNumber}*\n` +
      `${clientName}\n` +
      (caseTitle ? `Case: ${caseTitle}\n` : "") +
      `Amount: ${inr(totalAmount)}\n` +
      `Due: ${fmtDate(dueDate)}\n\n` +
      `— ${userName}`;
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <div className="flex h-[60vh] items-center justify-center text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </div>
    );
  }

  const labelCls = "block text-sm font-medium text-slate-600 mb-1.5";
  const fieldCls =
    "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40";

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-1.5 text-sm">
            <Link to="/portal/invoices" className="text-slate-500 hover:text-slate-800">
              Invoices
            </Link>
            <ChevronRight className="h-4 w-4 text-slate-400" />
            <span className="font-medium text-slate-800">
              {isEdit ? invoiceNumber || "Invoice" : "Create Invoice"}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setView(view === "form" ? "preview" : "form")}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
            >
              {view === "form" ? <Eye className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
              {view === "form" ? "Preview" : "Edit"}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => save(isEdit ? editStatus : "draft")}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-50"
            >
              <Save className="h-4 w-4" /> {isEdit ? "Save Changes" : "Save Draft"}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => save("sent", { email: true })}
              className="inline-flex items-center gap-2 rounded-lg bg-yellow-500 px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm transition-colors hover:bg-yellow-600 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send Invoice
            </button>
          </div>
        </div>

        {view === "preview" ? (
          <InvoicePreview
            invoiceNumber={invoiceNumber}
            invoiceDate={invoiceDate}
            dueDate={dueDate}
            billTo={billToObj}
            caseData={previewCase}
            caseTitle={caseTitle}
            items={withComputed}
            subTotal={subTotal}
            discountAmt={discountAmt}
            gst={gst}
            totalAmount={totalAmount}
            amountInWords={numberToWords(totalAmount)}
            onDownload={downloadPdf}
            onEmail={shareEmail}
            onWhatsApp={shareWhatsApp}
            onMarkPaid={() => save("paid")}
            saving={saving}
          />
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
            {/* ===== Left: form ===== */}
            <div className="space-y-4 lg:col-span-2">
              {/* Invoice Details */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-800">Invoice Details</h3>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label className={labelCls}>Invoice Number</label>
                    <input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} className={fieldCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Invoice Date</label>
                    <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} className={`${fieldCls} [color-scheme:light]`} />
                  </div>
                  <div>
                    <label className={labelCls}>Due Date <span className="text-red-500">*</span></label>
                    <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={`${fieldCls} [color-scheme:light]`} />
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>Client <span className="text-red-500">*</span></label>
                    <SelectMenu value={contactId} onChange={(e) => setContactId(e.target.value)} className={fieldCls}>
                      {clients.length === 0 && <option value="">Select a client</option>}
                      {clients.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.salutation ? `${c.salutation} ` : ""}{c.name}{c.company ? ` (${c.company})` : ""}
                        </option>
                      ))}
                    </SelectMenu>
                  </div>
                  <div>
                    <label className={labelCls}>Related Case</label>
                    <SelectMenu value={selectedCaseId} onChange={(e) => setSelectedCaseId(e.target.value)} className={fieldCls}>
                      <option value="">No case (custom)</option>
                      {cases.map((c) => (
                        <option key={c.id} value={c.id}>
                          {[c.petitionerName, c.respondentName].filter(Boolean).join(" vs ")} ({c.caseNo})
                        </option>
                      ))}
                    </SelectMenu>
                  </div>
                </div>

                {/* Bill To + Case summary */}
                {(selectedClient || selectedCase) && (
                  <div className="mt-4 grid grid-cols-1 gap-4 border-t border-slate-100 pt-4 sm:grid-cols-2">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Bill To</p>
                      <p className="mt-1 font-semibold text-slate-800">{clientName || "—"}</p>
                      {clientAddress(selectedClient) && (
                        <p className="text-sm text-slate-500">{clientAddress(selectedClient)}</p>
                      )}
                      {selectedClient?.gstin && <p className="text-sm text-slate-500">GSTIN: {selectedClient.gstin}</p>}
                    </div>
                    {selectedCase && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Case</p>
                        <p className="mt-1 font-semibold text-slate-800">{caseTitle}</p>
                        <p className="text-sm text-slate-500">Case No: {selectedCase.caseNo || "—"}</p>
                        {selectedCase.courtName && <p className="text-sm text-slate-500">Court: {selectedCase.courtName}</p>}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Itemized Details */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-800">Itemized Details</h3>
                <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200">
                  <table className="data-table w-full min-w-[640px] border-collapse text-left text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        <th className="py-3 pl-4 pr-2 w-8">#</th>
                        <th className="py-3 px-3">Description</th>
                        <th className="py-3 px-3">Related To</th>
                        <th className="py-3 px-3 w-16 text-right">Qty</th>
                        <th className="py-3 px-3 w-28 text-right">Amount (₹)</th>
                        <th className="py-3 px-3 w-24 text-right">Tax ({TAX_RATE}%)</th>
                        <th className="py-3 px-3 w-28 text-right">Total (₹)</th>
                        <th className="py-3 pl-2 pr-4 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {withComputed.map((it, idx) => (
                        <tr key={it.id} className="align-middle">
                          <td className="py-3 pl-4 pr-2 text-slate-400">{idx + 1}</td>
                          <td className="py-3 px-3">
                            <input value={it.description} onChange={(e) => updateItem(it.id, "description", e.target.value)} placeholder="e.g. Court fee" className={`${fieldCls} py-2`} />
                          </td>
                          <td className="py-3 px-3">
                            <input value={it.relatedTo} onChange={(e) => updateItem(it.id, "relatedTo", e.target.value)} placeholder="e.g. Hearing" className={`${fieldCls} py-2`} />
                          </td>
                          <td className="py-3 px-3">
                            <input type="text" inputMode="numeric" value={it.qty} onChange={(e) => /^\d*$/.test(e.target.value) && updateItem(it.id, "qty", e.target.value)} className={`${fieldCls} py-2 text-right`} />
                          </td>
                          <td className="py-3 px-3">
                            <input type="text" inputMode="decimal" value={it.amount} onChange={(e) => /^\d*\.?\d*$/.test(e.target.value) && updateItem(it.id, "amount", e.target.value)} placeholder="0.00" className={`${fieldCls} py-2 text-right`} />
                          </td>
                          <td className="py-3 px-3 text-right text-slate-600">{it.tax.toFixed(2)}</td>
                          <td className="py-3 px-3 text-right font-medium text-slate-800">{it.total.toFixed(2)}</td>
                          <td className="py-3 pl-2 pr-4">
                            <button type="button" onClick={() => removeItem(it.id)} className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600" title="Remove">
                              <Trash2 size={15} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button type="button" onClick={addItem} className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline">
                  <Plus size={15} /> Add Item
                </button>
              </div>
            </div>

            {/* ===== Right: summary ===== */}
            <div className="lg:col-span-1">
              <div className="sticky top-4 rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <h4 className="text-base font-bold text-slate-900">Summary</h4>
                <div className="mt-4 space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Sub Total</span>
                    <span className="font-medium text-slate-800">{inr(subTotal)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Discount (%)</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={discountPct}
                      onChange={(e) => /^\d*\.?\d*$/.test(e.target.value) && setDiscountPct(e.target.value)}
                      className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm text-slate-900 focus:border-yellow-400 focus:outline-none"
                    />
                  </div>
                  {discountAmt > 0 && (
                    <div className="flex items-center justify-between text-slate-500">
                      <span>Discount amount</span>
                      <span>- {inr(discountAmt)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">GST ({TAX_RATE}%)</span>
                    <span className="font-medium text-slate-800">{inr(gst)}</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-slate-200 pt-3">
                    <span className="font-semibold text-slate-700">Total Amount</span>
                    <span className="font-bold text-slate-900">{inr(totalAmount)}</span>
                  </div>
                </div>
                <div className="mt-4 border-t border-slate-200 pt-3">
                  <p className="text-xs text-slate-400">Amount in words</p>
                  <p className="mt-0.5 text-sm font-medium text-slate-700">{numberToWords(totalAmount)}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// ===== Printable preview =====
function InvoicePreview({
  invoiceNumber, invoiceDate, dueDate, billTo, caseData, caseTitle,
  items, subTotal, discountAmt, gst, totalAmount, amountInWords, onDownload, onEmail, onWhatsApp, onMarkPaid, saving,
}) {
  return (
    <div className="mt-4">
      <div className="mx-auto max-w-3xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-yellow-500 text-lg font-bold text-white">J</span>
              <div>
                <p className="text-lg font-bold text-slate-900">JURIDENT</p>
                <p className="text-xs text-slate-400">Legal. Simplified.</p>
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold tracking-tight text-slate-900">INVOICE</p>
            <p className="mt-1 text-sm text-slate-500">{invoiceNumber}</p>
            <p className="text-sm text-slate-500">Date: {fmtDate(invoiceDate)}</p>
            <p className="text-sm text-slate-500">Due: {fmtDate(dueDate)}</p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Bill To</p>
            <p className="mt-1 font-semibold text-slate-800">{billTo.name || "—"}</p>
            {billTo.address && <p className="text-sm text-slate-500">{billTo.address}</p>}
            {billTo.gstin && <p className="text-sm text-slate-500">GSTIN: {billTo.gstin}</p>}
          </div>
          {caseData && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Case Details</p>
              <p className="mt-1 font-semibold text-slate-800">{caseTitle}</p>
              <p className="text-sm text-slate-500">Case No: {caseData.caseNo || "—"}</p>
              {caseData.courtName && <p className="text-sm text-slate-500">Court: {caseData.courtName}</p>}
            </div>
          )}
        </div>

        <div className="mt-8 overflow-x-auto">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2 w-6">#</th>
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2">Related To</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-right">Amount (₹)</th>
                <th className="px-3 py-2 text-right">Tax (₹)</th>
                <th className="px-3 py-2 text-right">Total (₹)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.filter((it) => it.description || it.base > 0).map((it, idx) => (
                <tr key={it.id}>
                  <td className="px-3 py-2.5 text-slate-400">{idx + 1}</td>
                  <td className="px-3 py-2.5 font-medium text-slate-800">{it.description || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-500">{it.relatedTo || "—"}</td>
                  <td className="px-3 py-2.5 text-right text-slate-600">{it.qty}</td>
                  <td className="px-3 py-2.5 text-right text-slate-600">{it.base.toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-600">{it.tax.toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-right font-medium text-slate-800">{it.total.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex justify-end">
          <div className="w-full max-w-xs space-y-2 text-sm">
            <div className="flex justify-between text-slate-500"><span>Sub Total</span><span>{inr(subTotal)}</span></div>
            {discountAmt > 0 && <div className="flex justify-between text-slate-500"><span>Discount</span><span>- {inr(discountAmt)}</span></div>}
            <div className="flex justify-between text-slate-500"><span>GST</span><span>{inr(gst)}</span></div>
            <div className="flex justify-between border-t border-slate-200 pt-2 text-base font-bold text-slate-900"><span>Total Amount</span><span>{inr(totalAmount)}</span></div>
          </div>
        </div>

        <p className="mt-3 text-right text-xs text-slate-400">{amountInWords}</p>

        <div className="mt-8 flex items-end justify-between border-t border-slate-100 pt-4">
          <p className="text-sm text-slate-400">Thank you for your business!</p>
          <p className="text-sm text-slate-400">Authorized Signatory</p>
        </div>
      </div>

      {/* Preview actions */}
      <div className="mx-auto mt-4 flex max-w-3xl flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={onDownload} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
            <Download className="h-4 w-4" /> Download PDF
          </button>
          <button type="button" disabled={saving} onClick={onEmail} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50">
            <Mail className="h-4 w-4" /> Share via Email
          </button>
          <button type="button" onClick={onWhatsApp} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
            <MessageCircle className="h-4 w-4" /> Share via WhatsApp
          </button>
        </div>
        <button type="button" disabled={saving} onClick={onMarkPaid} className="inline-flex items-center gap-2 rounded-lg bg-yellow-500 px-5 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-yellow-600 disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save & Mark as Paid
        </button>
      </div>
    </div>
  );
}

export default CreateInvoice;
